import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';

/**
 * SuccessRateChart - Displays success vs error rate as a donut chart
 * Quick visual health check for run outcomes
 */
const SuccessRateChart = ({ runs, loading }) => {
  // Calculate success/error counts
  const chartData = useMemo(() => {
    if (!runs || runs.length === 0) return [];
    
    const successCount = runs.filter(r => r.status === 'success').length;
    const errorCount = runs.filter(r => r.status !== 'success').length;
    
    return [
      { name: 'Success', value: successCount, color: '#32CD32' },
      { name: 'Error', value: errorCount, color: '#EF4444' }
    ].filter(item => item.value > 0); // Only show segments with data
  }, [runs]);

  // Calculate success rate percentage
  const successRate = useMemo(() => {
    if (!runs || runs.length === 0) return 0;
    const successCount = runs.filter(r => r.status === 'success').length;
    return ((successCount / runs.length) * 100).toFixed(1);
  }, [runs]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = ((data.value / runs.length) * 100).toFixed(1);
      return (
        <div 
          className="rounded-lg p-3 shadow-lg bg-bg-secondary/95 backdrop-blur-sm"
          style={{ border: `1px solid ${data.color}40` }}
        >
          <p className="text-sm font-semibold" style={{ color: data.color }}>
            {data.name}
          </p>
          <p className="text-xs mt-1 text-text-primary">
            {data.value} runs ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array
  };

  // Custom legend
  const CustomLegend = ({ payload }) => {
    return (
      <div className="flex justify-center gap-6 mt-2">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ background: entry.color }}
            />
            <span className="text-xs text-text-secondary">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  };
  CustomLegend.propTypes = {
    payload: PropTypes.array
  };

  if (loading && (!runs || runs.length === 0)) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <div className="h-4 w-32 rounded mb-4 animate-pulse bg-success-50"></div>
        <div className="h-[200px] rounded animate-pulse flex items-center justify-center">
          <div className="w-32 h-32 rounded-full animate-pulse bg-primary-500/5 border-8 border-primary-500/10" />
        </div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <h3 className="text-sm font-semibold mb-4 text-text-primary">
          Success Rate
        </h3>
        <div className="h-[200px] flex items-center justify-center">
          <p className="text-sm text-text-tertiary">No run data available</p>
        </div>
      </div>
    );
  }

  // Determine color based on success rate
  const getRateColor = () => {
    const rate = parseFloat(successRate);
    if (rate >= 90) return 'text-primary-500';
    if (rate >= 70) return 'text-amber-500';
    return 'text-red-500';
  };

  return (
    <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-primary-500/25">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-text-primary">
          Success Rate
        </h3>
        <span className="text-xs text-text-tertiary">
          {runs.length} total runs
        </span>
      </div>
      
      <div className="h-[200px] relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={70}
              paddingAngle={2}
              dataKey="value"
              stroke="none"
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.color}
                  style={{ 
                    filter: entry.name === 'Success' 
                      ? 'drop-shadow(0 0 8px rgba(50, 205, 50, 0.3))' 
                      : 'drop-shadow(0 0 8px rgba(239, 68, 68, 0.3))' 
                  }}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
          </PieChart>
        </ResponsiveContainer>
        
        {/* Center label - positioned above legend */}
        <div 
          className="absolute left-0 right-0 flex items-center justify-center pointer-events-none"
          style={{ top: '50%', transform: 'translateY(-65%)' }}
        >
          <div className="text-center">
            <div className={`text-2xl font-bold ${getRateColor()}`}>
              {successRate}%
            </div>
            <div className="text-xs text-text-tertiary">
              success
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SuccessRateChart.propTypes = {
  runs: PropTypes.arrayOf(PropTypes.shape({
    status: PropTypes.string
  })),
  loading: PropTypes.bool
};

export default SuccessRateChart;
